/*
 * The Studio plan page: the one place its repeated facts live.
 *
 * Singulate is the product. Studio is the self-serve plan. The signup
 * destination, the sign-in page, the button label, the offer lines, and the
 * illustrative Maple Street Consulting example that the demonstration, the
 * legend and the review illustration all draw from. The example is fiction
 * and is labelled as such on the page; nothing here is a customer, a result,
 * or a quote.
 */

/** The creation flow. The early-access code is intentional through Oct 18, 2026. */
export const STUDIO_SIGNUP_URL = 'https://app.singulate.com/signup?code=singulate_early_access'

export const STUDIO_CTA_LABEL = 'Create your first email'

/* Chad's HubSpot scheduling page: "Singulate demo", 30 minutes. Every Book demo / Book a demo CTA ends here. */
export const DEMO_BOOKING_URL = 'https://meetings.hubspot.com/chad-rosh/demo'

/** The app's existing sign-in page; app.singulate.com redirects here. */
export const STUDIO_SIGNIN_URL = 'https://app.singulate.com/users/sign_in'

/** Under the hero buttons. */
export const STUDIO_OFFER_LINE = 'Free 14-day trial. Studio plan: $49/month at launch.'

/** Under the closing button. */
export const STUDIO_PRICE_LINE = 'Studio launch price: $49/month.'

/** The short trial line under a button: the hero, the phone CTA bar. */
export const STUDIO_TRIAL_SHORT = 'Free for 14 days, then $49 a month.'

/** Under the button on the price card: the charge and the way out, as checkout runs them today. */
export const STUDIO_TRIAL_TERMS =
  'Free for 14 days, then $49/month. Cancel before your trial ends to avoid being charged.'

/*
 * One contact record and what Singulate makes of it. `annotations` are the
 * legend lines, one per marker, written for this person. `name` is the raw value
 * in the record and `display` is what the greeting shows; the display form is
 * stored, not computed, because the product's formatting (a Wow Tag) is what
 * is being illustrated, not a case-conversion rule. `section` is the branched
 * paragraph for this person's history and `line` the generated sentence for
 * their role. `annotations` name what each marker points at, in marker order.
 */
export interface MaplePerson {
  id: string
  name: string
  display: string
  title: string
  info: string
  section: string
  /** A quiet secondary link under the section, preview content only. */
  sectionLink?: string
  line: string
  annotations: [string, string, string]
}

const ATTENDEE = 'This builds on our workshop on setting rates.'

export const MAPLE = {
  business: 'Maple Street Consulting',
  wordmark: 'Maple Street',
  /** Display text only, never a destination. */
  site: 'maplestreetconsulting.example',
  from: 'you@maplestreetconsulting.example',
  siteLabel: 'Website',
  briefLabel: 'Brief',
  brief: 'Invite people to our next workshop on pricing client projects.',
  label: 'Illustrative example',
  sender: 'Maple Street Consulting',
  subject: 'Next workshop: pricing a client project',
  preview: 'A practical session on scope, pricing, and explaining your quote.',
  headline: 'Turn your rates into a project quote.',
  closing: "We'll also cover how to explain your quote to a client.",
  cta: 'See the workshop',
  signature: 'Maple Street Consulting',
  defaultPerson: 'sarah',
  people: [
    {
      id: 'sarah',
      name: 'SARAH',
      display: 'Sarah',
      title: 'Brand strategist',
      info: 'Attended our workshop on setting rates.',
      section: ATTENDEE,
      line: 'Bring a brand strategy brief and price the research and recommendations.',
      annotations: [
        'SARAH becomes Sarah.',
        'Builds on the workshop she attended.',
        'A pricing exercise for brand strategy work.',
      ],
    },
    {
      id: 'marcus',
      name: 'Marcus',
      display: 'Marcus',
      title: 'Web designer',
      info: 'Registered for our workshop on setting rates, but did not attend.',
      section: "Here's the recording of the rates workshop, so you can catch up before we cover project quotes.",
      sectionLink: 'Watch the recording',
      line: 'Bring a website project and price the build, revisions, and handoff.',
      annotations: [
        'His name in the greeting.',
        'A recording of the session he missed.',
        'A pricing exercise for a website project.',
      ],
    },
    {
      id: 'priya',
      name: 'Priya',
      display: 'Priya',
      title: 'Marketing consultant',
      info: 'Attended our workshop on setting rates. Asked about fixed-fee projects.',
      section: ATTENDEE,
      line: 'You asked about fixed fees. Bring a campaign brief and price the planning and delivery.',
      annotations: [
        'Her name in the greeting.',
        'Builds on the workshop she attended.',
        'Connects the workshop to her fixed-fee question.',
      ],
    },
  ] satisfies MaplePerson[],
}

/*
 * The proof illustration: the supplied Click Rate Banner recreated in HTML
 * with Sarah's email, so its panels read from the same example. Wow Tag,
 * Branched Content and Snippet are the product's names for the three kinds of
 * personalization; the buyer-facing labels on the demonstration stay in the
 * spec.
 */
const SARAH = MAPLE.people[0]
export const MAPLE_ARTWORK = {
  alt: 'An email from Maple Street Consulting with a greeting, workshop context, and invitation line personalized for Sarah.',
  wowTag: { product: 'Wow Tag', label: 'Name, naturally formatted', input: SARAH.name, output: `Hi ${SARAH.display},` },
  branched: { product: 'Branched Content', audience: 'For people who attended', text: SARAH.section },
  snippet: { product: 'Snippet', audience: `For ${SARAH.display}, a ${SARAH.title.toLowerCase()}`, text: SARAH.line },
}
